import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../lib/store.ts';
import { showPaywall } from '../lib/plan.ts';
import { refreshPlan } from '../hooks/usePlan.ts';
import { ImportLinkForm } from './ImportLinkForm.tsx';
import {
  ACCEPT,
  type ImportJob,
  type UploadOutcome,
  importStatus,
  isFinished,
  isRideFile,
  isZip,
  startImport,
  startLinkImport,
  uploadRide,
} from '../lib/rideUpload.ts';

/** Where the dialog opens: ride files, or the history import (ZIP or link). */
export type UploadSection = 'files' | 'history';

/** How often a running history import is polled. */
export const IMPORT_POLL_MS = 2_000;
/** The API's history ZIP limit when a 413 doesn't say (``HISTORY_ZIP_MAX_MB``). */
export const DEFAULT_ZIP_MAX_MB = 95;

type FileStatus = 'waiting' | 'uploading' | UploadOutcome;

interface FileRow {
  key: string;
  name: string;
  status: FileStatus;
}

const STATUS_CLASS: Record<FileStatus, string> = {
  waiting: 'text-(--color-text-secondary)',
  uploading: 'text-(--color-text-secondary)',
  added: 'text-(--color-accent)',
  duplicate: 'text-(--color-text-secondary)',
  unreadable: 'text-red-400',
  quota: 'text-amber-300',
  failed: 'text-red-400',
};

/**
 * Upload ride files (.fit, .tcx, .gpx: one or many) or import a whole
 * history from a ZIP or a data-export download link (Pro). Files go up one by one with a status each;
 * duplicates are reported, not added twice. Free users see their monthly
 * allowance; going over it or picking a ZIP opens the paywall.
 */
export function UploadRidesDialog({ onClose, section = 'files' }: Readonly<{
  onClose: () => void;
  /** ``history``: opened from "Import your history", scrolls to the link form. */
  section?: UploadSection;
}>) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useStore((s) => s.user);
  const isPro = user?.plan === 'pro';
  const [rows, setRows] = useState<FileRow[]>([]);
  const [job, setJob] = useState<ImportJob | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (section === 'history') historyRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [section]);

  const setStatus = (key: string, status: FileStatus) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, status } : row)));

  const uploadFiles = useCallback(async (files: File[]) => {
    const added = files.map((file, i) => ({ key: `${Date.now()}-${i}`, name: file.name, status: 'waiting' as FileStatus }));
    setRows((current) => [...added, ...current]);
    let quotaHit = false;
    for (const [i, file] of files.entries()) {
      const { key } = added[i];
      if (quotaHit) {
        setStatus(key, 'quota');
        continue;
      }
      setStatus(key, 'uploading');
      const outcome = await uploadRide(file);
      quotaHit = outcome === 'quota';
      setStatus(key, outcome);
    }
  }, []);

  const runImport = useCallback(async (file: File) => {
    if (!isPro) {
      showPaywall('pro_feature');
      return;
    }
    setImportError(null);
    const started = await startImport(file);
    if (started.ok) {
      setJob(started.job);
    } else if (started.reason === 'running' && started.job) {
      setJob(started.job);
    } else if (started.reason === 'too_large') {
      setImportError(t('upload.import.tooLarge', { mb: started.maxMb ?? DEFAULT_ZIP_MAX_MB }));
    } else if (started.reason !== 'pro') {
      setImportError(t('upload.import.failed'));
    }
  }, [isPro, t]);

  const runLinkImport = useCallback(async (url: string) => {
    if (!isPro) {
      showPaywall('pro_feature');
      return;
    }
    setImportError(null);
    setBusy(true);
    try {
      const started = await startLinkImport(url);
      if (started.ok || (started.reason === 'running' && started.job)) {
        setJob(started.job ?? null);
      } else if (started.reason !== 'pro' && started.reason !== 'running') {
        setImportError(t(`upload.link.errors.${started.reason}`));
      }
    } finally {
      setBusy(false);
    }
  }, [isPro, t]);

  const handleFiles = useCallback(async (list: FileList | File[]) => {
    const files = Array.from(list);
    const zips = files.filter(isZip);
    const rides = files.filter(isRideFile);
    const others = files.filter((f) => !isZip(f) && !isRideFile(f));
    setBusy(true);
    try {
      if (others.length) {
        setRows((current) => [
          ...others.map((f, i) => ({ key: `x-${Date.now()}-${i}`, name: f.name, status: 'unreadable' as FileStatus })),
          ...current,
        ]);
      }
      if (rides.length) await uploadFiles(rides);
      if (zips.length) await runImport(zips[0]);
    } finally {
      setBusy(false);
      await refreshPlan();
      await qc.invalidateQueries({ queryKey: ['rides'] });
    }
  }, [qc, runImport, uploadFiles]);

  // Poll a running import until it finishes, then refresh the rides.
  useEffect(() => {
    if (!job || isFinished(job)) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const latest = await importStatus();
        if (cancelled || !latest) return;
        setJob(latest);
        if (isFinished(latest)) await qc.invalidateQueries({ queryKey: ['rides'] });
      } catch {
        // Offline for a moment: try again on the next tick.
      }
    };
    const timer = setTimeout(poll, job.status === 'queued' ? 0 : IMPORT_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [job, qc]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [onClose]);

  const freeLeft = user?.free_uploads_left;
  const freeMonthly = user?.free_uploads_monthly ?? 10;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4">
      <dialog
        open
        aria-modal="true"
        aria-labelledby="upload-title"
        className="static m-0 w-full max-w-lg rounded-2xl border border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) p-6 flex flex-col gap-4"
      >
        <h2 id="upload-title" className="text-xl font-bold">{t('upload.title')}</h2>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void handleFiles(e.dataTransfer.files);
          }}
          disabled={busy}
          className={`rounded-xl border-2 border-dashed p-6 text-sm text-center transition-colors ${
            dragging ? 'border-(--color-accent) bg-(--color-bg-tertiary)' : 'border-(--color-border)'
          }`}
        >
          <span className="block font-medium">{t('upload.drop')}</span>
          <span className="block mt-1 text-(--color-text-secondary)">{t('upload.hint')}</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          data-testid="upload-input"
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {freeLeft != null && (
          <p className="text-xs text-(--color-text-secondary)">
            {t('upload.freeLeft', { left: freeLeft, count: freeMonthly })}
          </p>
        )}
        <div ref={historyRef} data-testid="upload-history">
          <ImportLinkForm busy={busy} onImport={(url) => void runLinkImport(url)} />
        </div>
        {!isPro && <p className="text-xs text-(--color-text-secondary)">{t('upload.zipIsPro')}</p>}
        {job && <ImportProgress job={job} />}
        {importError && <p className="text-sm text-red-400">{importError}</p>}
        {rows.length > 0 && (
          <ul className="flex flex-col gap-1 max-h-56 overflow-y-auto text-sm">
            {rows.map((row) => (
              <li key={row.key} className="flex justify-between gap-3">
                <span className="truncate">{row.name}</span>
                <span className={`shrink-0 ${STATUS_CLASS[row.status]}`}>{t(`upload.status.${row.status}`)}</span>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={onClose}
          className="self-end px-5 py-2 rounded-xl text-sm font-semibold border border-(--color-border) hover:border-(--color-accent)"
        >
          {t('upload.close')}
        </button>
      </dialog>
    </div>
  );
}

function ImportProgress({ job }: Readonly<{ job: ImportJob }>) {
  const { t } = useTranslation();
  const counts = t('upload.import.counts', {
    imported: job.imported,
    duplicates: job.duplicates,
    skipped: job.skipped,
    failed: job.failed,
  });
  if (job.status === 'failed') {
    return <p className="text-sm text-red-400">{job.error ?? t('upload.import.failed')}</p>;
  }
  return (
    <output className="block rounded-xl bg-(--color-bg-tertiary) px-4 py-3 text-sm">
      <span className="block font-medium">
        {job.status === 'done' ? t('upload.import.done') : t('upload.import.running', { files: job.files_total })}
      </span>
      <span className="block text-(--color-text-secondary) mt-1">{counts}</span>
    </output>
  );
}
